import { getUser } from "../actions/auth";
import config from "../config";
import defaultConfig from "../config/default";
import { initializeAppTooltips } from "./interactions";
import { hasPermission } from "./permissions";

export const ZENDESK_SUBDOMAIN = (window.location.href.match(/staging|localhost/) !== null ? defaultConfig : config)
  .zendesk.subdomain;

export function percentage(num) {
  return num ? Number(`${num}`.replace("%", "")) / 100.0 : num;
}

export function number(num) {
  return num ? Number(`${num}`.replace(/[^0-9.-]+/g, "")) : num;
}

export function formatDate(days, format = "MM/DD/YYYY") {
  const date = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  switch (format) {
    case "MM/DD/YYYY":
    case "YYYY-MM-DD":
      return (
        date.getFullYear() +
        "-" +
        String(date.getMonth() + 1).padStart(2, 0) +
        "-" +
        String(date.getDate()).padStart(2, 0)
      );
    default:
      return (
        String(date.getMonth() + 1).padStart(2, 0) +
        "/" +
        String(date.getDate()).padStart(2, 0) +
        "/" +
        date.getFullYear()
      );
  }
}

export function formatDateString(date) {
  const dString = date.includes("T") ? date.replace("Z", "") : `${date}T00:00:00`;
  const d = new Date(dString);
  return `${String(d.getMonth() + 1).padStart(2, 0)}/${String(d.getDate()).padStart(2, 0)}/${d.getFullYear()}`;
}

export function compareDates(date1, date2, daysDiff = 0) {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return Math.ceil(Math.abs(d2 - d1) / (1000 * 60 * 60 * 24)) >= daysDiff;
}

export function formatPercentage(n, precision, defaultLabel = "0.00%") {
  if (!n) {
    return defaultLabel;
  }
  const decPlaces = precision !== undefined ? precision : 2;
  const symbol = "%";
  const thouSeparator = ",";
  const decSeparator = ".";
  const sign = n < 0 ? "-" : "";
  n = Math.abs((+n || 0) * 100).toFixed(decPlaces);
  const i = `${parseInt(n, 10)}`;
  const j = i.length > 3 ? i.length % 3 : 0;

  return (
    sign +
    (j ? i.slice(0, j) + thouSeparator : "") +
    i.slice(j).replace(/(\d{3})(?=\d)/g, `$1${thouSeparator}`) +
    (decPlaces
      ? decSeparator +
        Math.abs(n - i)
          .toFixed(decPlaces)
          .slice(2)
      : "") +
    symbol
  );
}

export function roundAndFormatCurrency(n) {
  return formatCurrency(round(n));
}

export function formatCurrency(n, defaultLabel = "$ 0.00") {
  if (!n) {
    return defaultLabel;
  }
  const decPlaces = 2;
  const currency = "$ ";
  const thouSeparator = ",";
  const decSeparator = ".";
  const sign = n < 0 ? "-" : "";
  n = Math.abs(+n || 0).toFixed(decPlaces);
  const i = `${parseInt(n, 10)}`;
  const j = i.length > 3 ? i.length % 3 : 0;

  return (
    currency +
    sign +
    (j ? i.slice(0, j) + thouSeparator : "") +
    i.slice(j).replace(/(\d{3})(?=\d)/g, `$1${thouSeparator}`) +
    (decPlaces
      ? decSeparator +
        Math.abs(n - i)
          .toFixed(decPlaces)
          .slice(2)
      : "")
  );
}

export function formatNumber(n, precision, defaultLabel = "0.00") {
  if (!n || n === "0.0") {
    return defaultLabel;
  }
  if (n.toString().match(/,/)) {
    return n;
  }
  const decPlaces = precision !== undefined ? precision : 2;
  const thouSeparator = ",";
  const decSeparator = ".";
  const sign = n < 0 ? "-" : "";
  n = Math.abs(+n || 0).toFixed(decPlaces);
  const i = `${parseInt(n, 10)}`;
  const j = i.length > 3 ? i.length % 3 : 0;

  return (
    sign +
    (j ? i.slice(0, j) + thouSeparator : "") +
    i.slice(j).replace(/(\d{3})(?=\d)/g, `$1${thouSeparator}`) +
    (decPlaces
      ? decSeparator +
        Math.abs(n - i)
          .toFixed(decPlaces)
          .slice(2)
      : "")
  );
}

export function getParameterByName(name, url) {
  if (!url) {
    url = window.location.href;
  }
  name = name.replace(/[[]]/g, "\\$&");
  const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return "";
  return decodeURIComponent(results[2].replace(/\+/g, " "));
}

function removeRawUrlParameter(serialized, prefix, name) {
  const raw = serialized.startsWith(prefix) ? serialized.slice(1) : serialized;
  const parameters = new URLSearchParams(raw);
  if (!parameters.has(name)) {
    return { serialized, value: null };
  }

  const value = parameters.get(name);
  const retained = raw
    .split("&")
    .filter((segment) => segment && !new URLSearchParams(segment).has(name))
    .join("&");
  return { serialized: retained ? `${prefix}${retained}` : "", value };
}

function sensitiveParameterStorageKey(pathname, name) {
  return `advisorspace:sensitive-url:${pathname}:${name}`;
}

const SENSITIVE_PARAMETER_SESSION_TTL_MS = 8 * 60 * 60 * 1000;

function storeSensitiveParameter(storageKey, value, maxAgeMs) {
  window.sessionStorage.setItem(
    storageKey,
    JSON.stringify({
      expiresAt: Date.now() + maxAgeMs,
      value,
    }),
  );
}

function readSensitiveParameter(storageKey) {
  const stored = window.sessionStorage.getItem(storageKey);
  if (stored === null) {
    return null;
  }

  try {
    const parsed = JSON.parse(stored);
    if (typeof parsed?.value === "string" && Number.isFinite(parsed?.expiresAt) && parsed.expiresAt > Date.now()) {
      return parsed.value;
    }
  } catch {
    window.sessionStorage.removeItem(storageKey);
    return null;
  }

  window.sessionStorage.removeItem(storageKey);
  return null;
}

export function consumeSensitiveUrlParameter(
  name,
  { preserveForSession = false, sessionMaxAgeMs = SENSITIVE_PARAMETER_SESSION_TTL_MS } = {},
) {
  const url = new URL(window.location.href);
  const fragment = removeRawUrlParameter(url.hash, "#", name);
  const query = removeRawUrlParameter(url.search, "?", name);
  const value = fragment.value;
  const storageKey = sensitiveParameterStorageKey(url.pathname, name);
  const sanitizedUrl = `${url.pathname}${query.serialized}${fragment.serialized}`;

  if (fragment.value !== null || query.value !== null) {
    window.history.replaceState(window.history.state, "", sanitizedUrl);
  }

  if (value === null) {
    if (preserveForSession) {
      try {
        return readSensitiveParameter(storageKey);
      } catch {
        return null;
      }
    }
    return null;
  }

  if (preserveForSession) {
    try {
      storeSensitiveParameter(storageKey, value, sessionMaxAgeMs);
    } catch {
      // URL cleanup still protects history when storage is unavailable.
    }
  }
  return value;
}

const parseUrlParams = (params, pairs) => {
  const pair = pairs[0];
  const parts = pair.split("=");
  const key = decodeURIComponent(parts[0]);
  const value = decodeURIComponent(parts.slice(1).join("="));
  params[key] = typeof params[key] === "undefined" ? value : [].concat(params[key], value);
  return pairs.length === 1 ? params : parseUrlParams(params, pairs.slice(1));
};

export function getUrlParams(search = window.location.search) {
  const query = search;
  return query.length === 0 ? {} : parseUrlParams({}, query.slice(1).split("&"));
}

export function shouldValidade(authorize, action) {
  const user = getUser();
  if (!user) {
    return false;
  }

  return hasPermission(authorize, action, user);
}

export function validateRole(roles = []) {
  const user = getUser();
  if (user) {
    const role = user.Role.id || -1;
    return roles.indexOf(role) !== -1;
  }

  return false;
}

export function round(num) {
  return +`${Math.round(`${num}e+2`)}e-2`;
}

export { initializePopovers as popover } from "./popover";

export function tooltip() {
  initializeAppTooltips();
}

const requestAnimFrame = (() =>
  window.requestAnimationFrame ||
  window.webkitRequestAnimationFrame ||
  window.mozRequestAnimationFrame ||
  ((callback) => {
    window.setTimeout(callback, 1000 / 60);
  }))();

export function scrollToY(scrollTargetY = 0, speed = 2000, easing = "easeOutSine") {
  const scrollY = window.scrollY || document.documentElement.scrollTop;
  let currentTime = 0;

  const time = Math.max(0.1, Math.min(Math.abs(scrollY - scrollTargetY) / speed, 0.8));

  const easingEquations = {
    easeOutSine: (pos) => Math.sin(pos * (Math.PI / 2)),
    easeInOutSine: (pos) => -0.5 * (Math.cos(Math.PI * pos) - 1),
    easeInOutQuint: (pos) => {
      if ((pos !== 0.5) < 1) {
        return 0.5 * pos ** 5;
      }
      return 0.5 * ((pos - 2) ** 5 + 2);
    },
  };

  function tick() {
    currentTime += 1 / 60;

    var p = currentTime / time;
    var t = easingEquations[easing](p);

    if (p < 1) {
      requestAnimFrame(tick);

      window.scrollTo(0, scrollY + (scrollTargetY - scrollY) * t);
    } else {
      window.scrollTo(0, scrollTargetY);
    }
  }

  tick();
}

export function getNested(obj, path, sep = ".") {
  try {
    return path
      .replace("[", sep)
      .replace("]", "")
      .split(sep)
      .reduce((obj, prop) => obj[prop], obj);
  } catch (_err) {
    return undefined;
  }
}

export function isNullOrUndefined(val) {
  return val === null || val === undefined;
}

export function getBody(object, files) {
  const body = new FormData();
  Object.keys(object).forEach((key) => {
    const val = files.indexOf(key) !== -1 ? getFile(key, object) : object[key];
    val !== null && val !== undefined && body.append(key, val);
  });

  return body;
}

function getFile(key, object) {
  if (object[key]) {
    return typeof object[key] === "string" ? object[key] : object[key][0];
  }
  return object[key];
}

export function arrayIsEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export function cancelFetchRequests(list) {
  for (const cancel of list) {
    if (cancel) {
      cancel();
    }
  }
}
